/**
 * Control-route access decision.
 *
 * The pure logic behind the <RequireControl> route guard, extracted so
 * it can be unit-tested without rendering the router. Mirrors the
 * backend's IsControlUser: control access requires BOTH a control
 * deployment AND a control-typed user.
 *
 * Returns one of:
 *   "wait" - deployment descriptor not loaded yet; render nothing
 *                 (do NOT fall through to a role check - audit V3).
 *   "login" - no authenticated user; send to /login.
 *   "deny" - authenticated but not allowed; send to / (home).
 *   "allow" - render the control page.
 */
export type ControlAccessDecision = "wait" | "login" | "deny" | "allow";

export interface ControlAccessInput {
  /** Is there an authenticated user? */
  hasUser: boolean;
  /** The user's user_type, if known. */
  userType: string | null | undefined;
  /** Has the deployment descriptor finished loading? */
  deploymentLoading: boolean;
  /** Is this a control deployment? (false until descriptor loads) */
  isControlDeployment: boolean;
}

/** Pure logic behind the <RequireControl> route guard - decides if a user may see control-plane routes. */
export function decideControlAccess(
  input: ControlAccessInput,
): ControlAccessDecision {
  // No user at all → login. (Checked first: an unauthenticated user
  // should go to login regardless of deployment state.)
  if (!input.hasUser) return "login";

  // Descriptor not loaded → wait. We must NOT fall through to the role
  // check here: doing so briefly rendered control content for a
  // control user on a client deployment before the descriptor
  // resolved (audit V3).
  if (input.deploymentLoading) return "wait";

  // Control plane requires a control deployment...
  if (!input.isControlDeployment) return "deny";
  // ...AND a control-typed user.
  if (input.userType !== "control") return "deny";

  return "allow";
}
